/**
 * Prefetch Service — caches dropdown/reference data in IndexedDB
 * so forms work offline even if the specific page was never loaded.
 *
 * Data is keyed by (key, scope) where:
 *   - key: data type (e.g. 'closeCallCategories', 'allCompanies', 'allUsers')
 *   - scope: 'global' or a project ID
 *
 * Usage:
 *   - Call prefetchGlobalData() after login
 *   - Call prefetchProjectData(projectId, { enabledTools }) when entering a project
 *   - Call getCachedReferenceData(key, scope) to read from cache (used by pages as fallback)
 */

import { db } from '../utils/db';
import api from '../utils/api';
import { getAllCompanies } from '../api/company';
import { getAssetProjectScope } from '../utils/offlineScope';

const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

// ─── Write ───────────────────────────────────────────────────────

/**
 * Save reference data to IndexedDB. Exported for pages that want to cache data for offline (e.g. briefing invitations).
 */
export async function saveReferenceData(key, scope, data) {
  try {
    const existing = await db.referenceData.get({ key, scope });
    const record = {
      key,
      scope,
      data: JSON.stringify(data),
      updated_at: Date.now()
    };
    if (existing) {
      await db.referenceData.put(record);
    } else {
      await db.referenceData.add(record);
    }
  } catch (err) {
    console.warn(`[prefetch] Failed to save ${key}/${scope}:`, err);
  }
}

// ─── Read ────────────────────────────────────────────────────────

/**
 * Get cached reference data from IndexedDB.
 * @param {string} key - Data type key
 * @param {string} scope - 'global' or project ID
 * @param {boolean} ignoreExpiry - Return even if expired (for offline)
 * @returns {Array|Object|null} The cached data, or null
 */
export async function getCachedReferenceData(key, scope = 'global', ignoreExpiry = false) {
  try {
    const record = await db.referenceData.get({ key, scope });
    if (!record) return null;

    const expired = Date.now() - record.updated_at > CACHE_TTL;
    if (expired && !ignoreExpiry) return null;

    return JSON.parse(record.data);
  } catch (err) {
    console.warn(`[prefetch] Failed to read ${key}/${scope}:`, err);
    return null;
  }
}

// ─── Prefetch: Global Data (called after login) ─────────────────

/**
 * Prefetch global reference data that's shared across all projects.
 * Call this after login succeeds.
 */
export async function prefetchGlobalData() {
  const tasks = [
    // Close Call categories
    fetchAndCache('closeCallCategories', 'global', async () => {
      const res = await api.get('/close-calls/categories');
      return (res.data.data || []).map(cat => cat.name).filter(Boolean);
    }),

    // Good Call categories
    fetchAndCache('goodCallCategories', 'global', async () => {
      const res = await api.get('/good-calls/categories');
      return res.data.data || [];
    }),

    // Job roles (for profiles and inductions)
    fetchAndCache('jobRoles', 'global', async () => {
      const res = await api.get('/job-roles');
      if (res.data?.success && Array.isArray(res.data.data)) {
        return (res.data.data || []).map((r) => (typeof r === 'string' ? r : r?.name)).filter(Boolean);
      }
      return [];
    }),

    // Competency/certificate types (for inductions and mandatory competencies)
    fetchAndCache('competencyTypes', 'global', async () => {
      const res = await api.get('/competency-types');
      if (res.data?.success && Array.isArray(res.data.data)) {
        return (res.data.data || []).map((r) => (typeof r === 'string' ? r : r?.name)).filter(Boolean);
      }
      return [];
    }),

    // All companies
    fetchAndCache('allCompanies', 'global', async () => {
      return await getAllCompanies();
    }),

    // All users
    fetchAndCache('allUsers', 'global', async () => {
      const res = await api.get('/auth/users');
      if (res.data.success && Array.isArray(res.data.data)) {
        return res.data.data.map(u => ({
          id: u.id,
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          email: u.email || '',
          first_name: u.first_name,
          last_name: u.last_name
        }));
      }
      return [];
    }),

    // Inspection templates
    fetchAndCache('inspectionTemplates', 'global', async () => {
      const res = await api.get('/inspections/templates', { params: { active: true } });
      if (res.data.success) {
        return (res.data.data || []).map(t => ({
          id: t.id,
          name: t.title || t.inspection_ref
        }));
      }
      return [];
    })
  ];

  await Promise.allSettled(tasks);
}

// ─── Prefetch: Project-Specific Data ─────────────────────────────

/**
 * Prefetch project-specific reference data.
 * Call this when entering a project dashboard.
 * @param {string} projectId - The project UUID
 */
export async function prefetchProjectData(projectId, options = {}) {
  if (!projectId) return;

  const enabledTools = Array.isArray(options.enabledTools) ? options.enabledTools : null;
  const hasTool = (key) => enabledTools == null || enabledTools.includes(key);
  const includeAssetManagement =
    options.includeAssetManagement === true || hasTool('assetManagement');
  const includeCloseCalls = hasTool('closeCalls');
  const includeGoodCalls = hasTool('goodCalls');
  const includeRams = hasTool('rams');
  const includeInductionsList =
    hasTool('inductions') || hasTool('inspections') || hasTool('closeCalls');

  const tasks = [];

  if (includeCloseCalls) {
    tasks.push(
      fetchAndCache('closeCallAssignedTo', projectId, async () => {
        const res = await api.get(`/close-calls/projects/${projectId}/assigned-to`);
        return res.data.data || [];
      }),
      fetchAndCache('closeCallInductedUsers', projectId, async () => {
        const res = await api.get(`/close-calls/projects/${projectId}/inducted-users`);
        return res.data.data || [];
      }),
      fetchAndCache('closeCallRaisedBy', projectId, async () => {
        const res = await api.get(`/close-calls/projects/${projectId}/raised-by`);
        return res.data.data || [];
      }),
      fetchAndCache('closeCallCompanies', projectId, async () => {
        const res = await api.get(`/close-calls/projects/${projectId}/companies`);
        return res.data.data || [];
      }),
      fetchAndCache('closeCallEmployers', projectId, async () => {
        const res = await api.get(`/close-calls/projects/${projectId}/employers`);
        return (res.data.data || []).map(emp => ({ name: emp }));
      }),
      fetchAndCache('closeCallTaggedCompanies', projectId, async () => {
        const res = await api.get(`/close-calls/projects/${projectId}/tagged-companies`);
        return (res.data.data || []).map(comp => ({ name: comp }));
      })
    );
  }

  if (includeGoodCalls) {
    tasks.push(
      fetchAndCache('goodCallRaisedBy', projectId, async () => {
        const res = await api.get(`/good-calls/projects/${projectId}/raised-by`);
        return res.data.data || [];
      }),
      fetchAndCache('goodCallCompanies', projectId, async () => {
        const res = await api.get(`/good-calls/projects/${projectId}/companies`);
        return res.data.data || [];
      })
    );
  }

  if (includeInductionsList) {
    tasks.push(
      fetchAndCache('inductedUsers', projectId, async () => {
        const res = await api.get(`/site-induction/projects/${projectId}/list`, {
          params: { limit: 1000, status: 'approved' }
        });
        if (res.data.data && Array.isArray(res.data.data)) {
          const usersMap = new Map();
          res.data.data.forEach(ind => {
            if (ind.user_id && !usersMap.has(ind.user_id)) {
              usersMap.set(ind.user_id, {
                id: ind.user_id,
                name: ind.user_name || ind.email || 'Unknown User',
                email: ind.email || ''
              });
            }
          });
          return Array.from(usersMap.values());
        }
        return [];
      })
    );
  }

  if (includeRams) {
    tasks.push(
      fetchAndCache('ramsUsers', projectId, async () => {
        const res = await api.get(`/rams/projects/${projectId}/users`);
        return res.data.data || [];
      })
    );
  }

  // Asset Management offline warm — only when the user actually has AM on this project
  if (includeAssetManagement) {
    tasks.push(
      fetchAndCache('assetRegister_plant-and-machinery', getAssetProjectScope(projectId), async () => {
        const res = await api.get(`/asset-management/projects/${projectId}/plant-and-machinery`);
        return res.data?.data || [];
      }),
      fetchAndCache('assetRegister_equipment', getAssetProjectScope(projectId), async () => {
        const res = await api.get(`/asset-management/projects/${projectId}/equipment`);
        return res.data?.data || [];
      }),
      fetchAndCache('assetRegister_access-equipment', getAssetProjectScope(projectId), async () => {
        const res = await api.get(`/asset-management/projects/${projectId}/access-equipment`);
        return res.data?.data || [];
      }),
      fetchAndCache('assetRegister_temporary-works', getAssetProjectScope(projectId), async () => {
        const res = await api.get(`/asset-management/projects/${projectId}/temporary-works`);
        return res.data?.data || [];
      }),
      fetchAndCache('assetTypes_plant-and-machinery', getAssetProjectScope(projectId), async () => {
        const res = await api.get(`/asset-management/projects/${projectId}/plant-and-machinery/types`);
        return res.data?.data || [];
      }),
      fetchAndCache('assetTypes_equipment', getAssetProjectScope(projectId), async () => {
        const res = await api.get(`/asset-management/projects/${projectId}/equipment/types`);
        return res.data?.data || [];
      }),
      fetchAndCache('assetTypes_access-equipment', getAssetProjectScope(projectId), async () => {
        const res = await api.get(`/asset-management/projects/${projectId}/access-equipment/types`);
        return res.data?.data || [];
      }),
      fetchAndCache('assetTypes_temporary-works', getAssetProjectScope(projectId), async () => {
        const res = await api.get(`/asset-management/projects/${projectId}/temporary-works/types`);
        return res.data?.data || [];
      })
    );
  }

  await Promise.allSettled(tasks);

  // Download daily-check contexts for on-site assets so yard operatives can work offline
  if (includeAssetManagement) {
    import('../utils/assetManagementOffline')
      .then((m) => m.prefetchAllScanContextsForProject(projectId))
      .catch(() => {});
  }
}

// ─── Helper ──────────────────────────────────────────────────────

async function fetchAndCache(key, scope, fetchFn) {
  try {
    const data = await fetchFn();
    await saveReferenceData(key, scope, data);
  } catch (err) {
    // Don't throw — we want other tasks to continue
    console.warn(`[prefetch] Failed to fetch ${key}/${scope}:`, err.message);
  }
}

/**
 * Clear all cached reference data.
 */
export async function clearReferenceData() {
  try {
    await db.referenceData.clear();
  } catch (err) {
    console.warn('[prefetch] Failed to clear reference data:', err);
  }
}
